/**
 * The shared renderer for GoodSender's legal pages (`/privacy`, `/terms`):
 * a Markdown file under `src/content/legal/` rendered to HTML, plus the parser
 * for its "Last updated: Month D, YYYY" line.
 *
 * Imported only from the legal pages' own modules (`privacy-policy.ts`,
 * `terms-of-service.ts`), which are imported from their route components
 * alone, so markdown-it travels in those code-split chunks and never in the
 * entry chunk (see `privacy-policy.ts`).
 */

import MarkdownIt from "markdown-it";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * A legal document's "Last updated: Month D, YYYY" line as YYYY-MM-DD. `\r?`
 * because nothing in the repo pins line endings and `$` under the `m` flag
 * stops before a carriage return. Each page's test asserts the result equals
 * the page's `*_LAST_UPDATED` constant, which the sitemap and the structured
 * data read, so the page, the sitemap and the JSON-LD agree.
 */
export function legalLastUpdated(markdown: string, fileName: string): string {
  const match = markdown.match(/^Last updated: ([A-Z][a-z]+) (\d{1,2}), (\d{4})\r?$/m);
  const month = match ? MONTHS.indexOf(match[1]) : -1;
  if (!match || month === -1) {
    throw new Error(`${fileName} must carry a 'Last updated: Month D, YYYY' line`);
  }
  return `${match[3]}-${String(month + 1).padStart(2, "0")}-${match[2].padStart(2, "0")}`;
}

/*
 * A renderer of its own rather than `@/lib/markdown`'s: that one mirrors the
 * blog renderer for authored posts (footnotes, task lists, figure captions),
 * none of which a legal page uses, and it deliberately does not linkify because
 * a post's host is not known at composing time. A legal page's URLs and
 * addresses are contacts and references and should be clickable. `html: false`
 * keeps the same stored-XSS posture as every other renderer in the repo.
 * `fuzzyLink` is off: linkify-it's default turns any bare `word.tld` into an
 * `http://` anchor, which made "Dev.to" in the list of platforms a link to that
 * site and every bare "goodsender.com" an insecure-scheme link (review
 * finding). Only explicit `https://…` URLs and e-mail addresses are linked. The
 * table wrapper mirrors `markdown.ts` so a wide table scrolls on a phone
 * instead of forcing the page wider.
 */
const md = new MarkdownIt({ html: false, linkify: true });
md.linkify.set({ fuzzyLink: false, fuzzyIP: false });
md.renderer.rules.table_open = (tokens, idx, options, _env, self) =>
  `<div class="gs-table-scroll" tabindex="0" role="region" aria-label="Table">` +
  self.renderToken(tokens, idx, options);
md.renderer.rules.table_close = (tokens, idx, options, _env, self) =>
  `${self.renderToken(tokens, idx, options)}</div>\n`;

/**
 * Source-only notes. A legal draft carries reviewer notes as HTML comments
 * (`<!-- DRAFT — … -->`); with `html: false` markdown-it would escape them into
 * visible text, so they are removed before rendering. Every other piece of raw
 * HTML is still escaped, never injected.
 */
const SOURCE_COMMENT = /<!--[\s\S]*?-->/g;

/** Exposed for the tests that prove raw HTML is escaped and notes stay hidden. */
export function renderLegalMarkdown(markdown: string): string {
  return md.render(markdown.replace(SOURCE_COMMENT, ""));
}
